import type Pusher from 'pusher-js';
import { getActiveToken } from '~/helpers/authToken';

/**
 * One Pusher connection per tab, shared by every subscriber.
 *
 * `usePusherSubscribe` used to open a connection per hook instance. Two
 * screens listening at once were two sockets — Pusher counts connections, and
 * `toOthers()` on the server can only leave out a sender it can name, which
 * needs *the* socket id rather than one of several. So the client lives here,
 * reference counted: the first subscriber connects, the last one disconnects.
 *
 * Private channels authorise against Laravel's `/broadcasting/auth` with the
 * same bearer token the API uses, read at authorisation time rather than at
 * connect time so a refreshed token is the one that is sent.
 */
let client: Pusher | null = null;
let pending: Promise<Pusher | null> | null = null;
let holders = 0;
let socketId: string | null = null;

type StateListener = (connected: boolean) => void;
const stateListeners = new Set<StateListener>();

/** Where private channels are authorised: the backend root, not the API prefix. */
const authEndpoint = () => {
    const apiHost = process.env.NEXT_PUBLIC_API_HOST ?? '';

    return apiHost.replace(/api\/?$/, '') + 'broadcasting/auth';
};

const connect = async (): Promise<Pusher | null> => {
    const key = process.env.NEXT_PUBLIC_PUSHER_APP_KEY;
    if (!key) return null;

    // Dynamic import keeps the client out of the initial bundle — most pages never open a socket.
    const { default: PusherClient } = await import('pusher-js');

    const instance = new PusherClient(key, {
        cluster: process.env.NEXT_PUBLIC_PUSHER_APP_CLUSTER ?? 'eu',
        channelAuthorization: {
            transport: 'ajax',
            endpoint: authEndpoint(),
            customHandler: ({ socketId: socket, channelName }, callback) => {
                const token = getActiveToken();

                fetch(authEndpoint(), {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({ socket_id: socket, channel_name: channelName })
                })
                    .then(async (response) => {
                        if (!response.ok) throw new Error(`broadcasting/auth ${response.status}`);
                        callback(null, await response.json());
                    })
                    .catch((error: Error) => callback(error, null));
            }
        }
    });

    instance.connection.bind('connected', () => {
        socketId = instance.connection.socket_id;
        stateListeners.forEach((listener) => listener(true));
    });
    instance.connection.bind('state_change', ({ current }: { current: string }) => {
        if ('connected' !== current) {
            if ('disconnected' === current || 'failed' === current) socketId = null;
            stateListeners.forEach((listener) => listener(false));
        }
    });

    return instance;
};

/** Hands out the shared client, connecting on first use. Pair with `releasePusher`. */
export const acquirePusher = async (): Promise<Pusher | null> => {
    holders += 1;

    // Wanted again before the linger ran out: keep the connection.
    if (lingering) {
        clearTimeout(lingering);
        lingering = null;
    }

    if (client) return client;
    if (!pending) {
        pending = connect()
            .then((instance) => {
                client = instance;
                pending = null;
                return instance;
            })
            // A failed connect must not leave a rejected promise behind: every
            // later subscriber awaits this same one and would fail with it.
            .catch(() => {
                pending = null;
                return null;
            });
    }

    const instance = await pending;

    // Released again before the connection was up — nobody wants it any more.
    if (0 === holders && instance) {
        instance.disconnect();
        client = null;
        socketId = null;
        return null;
    }

    return instance;
};

/**
 * How long an unwanted connection is kept before it is closed.
 *
 * Switching plans releases the old room's hold a moment before the new one is
 * taken, and tearing the socket down in that gap costs a reconnect, a fresh
 * authorisation and a leave-and-join everybody else sees. Two seconds covers
 * the gap; a tab that really closes the planner is gone before it matters.
 */
const LINGER_MS = 2000;
let lingering: ReturnType<typeof setTimeout> | null = null;

const disconnectNow = () => {
    if (!client) return;
    client.disconnect();
    client = null;
    socketId = null;
    stateListeners.forEach((listener) => listener(false));
};

export const releasePusher = () => {
    holders = Math.max(0, holders - 1);

    if (0 === holders && client && !lingering) {
        lingering = setTimeout(() => {
            lingering = null;
            if (0 === holders) disconnectNow();
        }, LINGER_MS);
    }
};

/**
 * The socket this tab speaks through, for the `X-Socket-Id` header on writes —
 * what lets the server broadcast a change to everybody but its author. Null
 * before the first subscription, which the server reads as "tell everyone".
 */
export const getPusherSocketId = () => socketId;

/** Watches whether the shared connection is up. Returns the unsubscribe. */
export const onPusherConnectionState = (listener: StateListener) => {
    stateListeners.add(listener);
    listener(null !== socketId);

    return () => {
        stateListeners.delete(listener);
    };
};
